import { Radio, Tooltip } from 'antd';
import { LOCALE } from 'constants/intl-wrapper';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { ContextWrapper } from 'contexts/wrapper.context';
import { ENGLISH_CHARS, isArabic } from 'utils/helpers';
import { LANGUAGES_OPTIONS } from 'constants/bilingual-input';

import BRButton from 'components/BRButton/BRButton';

import './BRBilingualOptions.less';

let isOptionsClickedForFirstTime = false;

const BRBilingualOptions = ({
  defaultLang,
  onChangeFieldLanguage = () => {},
  inputValue,
  isInputFocused,
  validateOnInput = true,
  getPopupContainer,
  isFieldTouched = false,
  isFromBilingualInput = false
}) => {
  const intl = useIntl();
  const { businessInfo, handleUpdateFeaturesConfigurations } =
    useContext(ContextWrapper);

  const storeInfo = businessInfo?.storeInfo;

  const [choosenLang, setChoosenLang] = useState(
    defaultLang || storeInfo?.defaultLang || LOCALE.AR
  );
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [tooltipData, setTooltipData] = useState({
    [LOCALE.EN]: {},
    [LOCALE.AR]: {}
  });

  const handleTooltipIntro = () =>
    setTooltipData({
      ...tooltipData,
      [LOCALE.AR]: {
        isVisible: true,
        content: (
          <>
            <div>
              {intl.formatMessage({
                id: 'bilingual_input.first_time_to_view'
              })}
            </div>
            <div>
              <BRButton
                type="link-color"
                className="br-bilingual-input__got-it"
                label={intl.formatMessage({ id: 'common.got_it' })}
                onClick={() => {
                  handleUpdateFeaturesConfigurations('isBilingualInputViewed');
                  resetTooltipVisibilty();
                  checkOnInputLang();
                }}
              />
            </div>
          </>
        )
      }
    });

  useEffect(() => {
    if (businessInfo?.featuresConfigurations) {
      if (
        !businessInfo?.featuresConfigurations?.isBilingualInputViewed &&
        isOptionsClickedForFirstTime
      ) {
        handleTooltipIntro();
        isOptionsClickedForFirstTime = true;
      }
    }
  }, [businessInfo, choosenLang, isOptionsClickedForFirstTime]);

  const resetTooltipVisibilty = () => {
    setTooltipData({
      [LOCALE.AR]: {
        ...tooltipData[LOCALE.AR],
        isVisible: false
      },
      [LOCALE.EN]: {
        ...tooltipData[LOCALE.EN],
        isVisible: false
      }
    });
  };

  useEffect(() => {
    if (!isInputFocused) {
      resetTooltipVisibilty();
    }
  }, [isInputFocused]);

  const onChangeLanguage = ({ target: { value } }) => {
    resetTooltipVisibilty();
    onChangeFieldLanguage({ value });
    setChoosenLang(value);
    if (!isOptionsClickedForFirstTime) {
      isOptionsClickedForFirstTime = true;
    }
  };

  const handleHTMLInText = () => {
    var a = document.createElement('div');
    a.innerHTML = inputValue[choosenLang];
    for (var c = a.childNodes, i = c.length; i--; ) {
      if (c[i].nodeType === 1) {
        return a.innerText;
      }
    }

    return inputValue[choosenLang];
  };

  const checkOnInputLang = async () => {
    if (!validateOnInput || !isInputFocused) {
      return;
    } else if (
      !businessInfo?.featuresConfigurations?.isBilingualInputViewed &&
      isOptionsClickedForFirstTime
    ) {
      handleTooltipIntro();
      return;
    }
    const valueWithoutSpecialChars = handleHTMLInText()?.replace(
      /[^a-zA-Z ]/g,
      ''
    );
    if (!inputValue[choosenLang]) {
      resetTooltipVisibilty();
    } else if (choosenLang === LOCALE.EN && isArabic(handleHTMLInText())) {
      setTooltipData({
        [LOCALE.AR]: {
          isVisible: true,
          content: intl.formatMessage({ id: 'bilingual_input.arabic_tooltip' })
        },
        [LOCALE.EN]: {
          ...tooltipData[LOCALE.EN],
          isVisible: false
        }
      });
    } else if (
      choosenLang === LOCALE.AR &&
      ENGLISH_CHARS.test(valueWithoutSpecialChars)
    ) {
      setTooltipData({
        [LOCALE.AR]: {
          ...tooltipData[LOCALE.AR],
          isVisible: false
        },
        [LOCALE.EN]: {
          isVisible: true,
          content: intl.formatMessage({ id: 'bilingual_input.english_tooltip' })
        }
      });
    } else {
      resetTooltipVisibilty();
    }
    return inputValue;
  };

  useEffect(() => {
    checkOnInputLang();
  }, [inputValue]);

  useEffect(() => {
    if (inputValue[LOCALE.AR] || inputValue[LOCALE.EN]) {
      if (!inputValue[defaultLang] && !isFieldTouched && isInitialRender) {
        if (
          defaultLang === LOCALE.EN &&
          !inputValue[LOCALE.EN] &&
          inputValue[LOCALE.AR]
        ) {
          onChangeFieldLanguage({ value: LOCALE.AR });
          setChoosenLang(LOCALE.AR);
        } else if (
          defaultLang === LOCALE.AR &&
          !inputValue[LOCALE.AR] &&
          inputValue[LOCALE.EN]
        ) {
          setChoosenLang(LOCALE.EN);
          onChangeFieldLanguage({ value: LOCALE.EN });
        }
        setIsInitialRender(false);
      }
    }
  }, [inputValue, defaultLang, isFieldTouched]);

  useEffect(() => {
    if (businessInfo?._id && !isFieldTouched && !isFromBilingualInput) {
      setChoosenLang(storeInfo?.defaultLang || LOCALE.AR);
    }
  }, [businessInfo, isFieldTouched]);

  return (
    <Radio.Group
      value={choosenLang}
      onChange={onChangeLanguage}
      className="br-bilingual-input__languages-options"
    >
      {LANGUAGES_OPTIONS.map(({ value, label }) => (
        <Tooltip
          overlayClassName="br-lang-options-tooltip font-family__default"
          title={tooltipData?.[value].content}
          visible={tooltipData?.[value].isVisible}
          getPopupContainer={
            getPopupContainer
              ? getPopupContainer
              : (triggerNode) =>
                  triggerNode.parentElement.parentElement.parentElement
                    .parentElement.parentElement.parentElement.parentElement
                    .parentElement.parentElement.parentElement
          }
          placement="top"
        >
          <Radio.Button
            value={value}
            className={classnames({
              'br-bilingual-input__languages-options__focused': !!isInputFocused
            })}
          >
            {label}
          </Radio.Button>
        </Tooltip>
      ))}
    </Radio.Group>
  );
};

export default BRBilingualOptions;
